import { useCallback, useState } from 'react';
import { saveAs } from 'file-saver';
import { FetchError, FetchRequest } from './api/api';
import { apiResolve } from './api/apiResolve';

export function useDownloadBlob(response: Response | undefined) {
  const [blob, setBlob] = useState<Blob | undefined>();

  const downloadDocument = useCallback(
    async (fileName: string) => {
      if (!response) {
        return;
      }
      let blobToSave: Blob | undefined = blob;
      if (!blob) {
        blobToSave = await response.blob();
        setBlob(blobToSave);
      }
      blobToSave && saveAs(blobToSave, fileName);
    },
    [blob, response]
  );

  return { blob, setBlob, downloadDocument };
}

// download blob using `apiResolve` to avoid useEffect workaround

export interface DownloadRequestOptions {
  // use existing request and get response (e.g. `api.post(...)`)
  request?: FetchRequest;
  // use existing response (can come from a fetch hook)
  response?: Response;
}

export function useDownloadRequest() {
  const [error, setError] = useState<FetchError>();
  const [isLoading, setIsLoading] = useState(false);

  const getResponse = useCallback(async (options: DownloadRequestOptions) => {
    // if response was not provided, do request
    const { request, response } = options;
    if (response) {
      return response;
    } else if (!request) {
      return;
    }
    const result = await apiResolve(request, { bodyParse: false });
    if (result.error) {
      setError(result.error);
    }
    return result.response;
  }, []);

  const download = useCallback(
    async (fileName: string, options: DownloadRequestOptions) => {
      try {
        setError(undefined);
        setIsLoading(true);
        const response = await getResponse(options);
        // download blob here instead of inside useEffect
        if (response?.ok && !response.bodyUsed) {
          saveAs(await response.blob(), fileName);
          return true;
        }
      } catch (error) {
        setError({
          statusCode: 400,
          error: error instanceof Error ? error.name : 'Error',
          message:
            error instanceof Error
              ? error.message
              : 'An error occurred while downloading the file.'
        });
      } finally {
        setIsLoading(false);
      }
      return false;
    },
    [getResponse]
  );

  return { isLoading, error, download };
}
